import { createContext, useContext, useState, useEffect, useRef } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import axiosBaseURL from "../api";
import { useNavigate } from "react-router-dom";

interface IUser {
  description: string;
  fullName: string;
  mfiId: string;
  nfcKey: string;
  resources: string[];
  roleId: string;
  roleName: string;
  status: number;
  token: string;
  tokenExpiryTime: string;
  userId: string;
  userName: string;
  isFirstLogin?: number;
  isActive?: number;
  phoneNo?: string;
  avatarUrl?: string;
}

interface ILoginError {
  status: number;
}
export type AuthContextValue = {
  user: IUser | undefined | null;
  setUser: (user: IUser | undefined | null) => void;
  authString: string;
  isLogedIn: boolean | undefined;
  signingIn: boolean;
  loginError: ILoginError;
  login: (userName: string, password: string) => void;
  logout: () => void;
  getUserByTolken: () => void;
  changePassword: (
    header: string,
    body: {
      userName: string;
      hashedOldPassword: string;
      hashedNewPassword: string;
    }
  ) => { isError: boolean; isSuccess: boolean };
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }: any) => {
  const [authString, setAuthString] = useState("");
  const [userName, setUserName] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [user, setUser] = useState<IUser | null | undefined>(null);
  const [isLogedIn, setIsLogedIn] = useState<boolean | undefined>();
  const [loginError, setLoaginError] = useState<ILoginError>({ status: -1 });

  const signin = async () => {
    setIsLogedIn(true);
    const result = await axiosBaseURL.get("/fe/login", {
      headers: { Authorization: authString },
    });

    const token = result.data.token;
    const userId = result.data.userId;

    if (token) {
      localStorage.setItem("token", `${userId}/${token}`);
    }
    return result;
  };

  const signout = (token: string) => {
    const result = axiosBaseURL.get("/fe/logout", {
      headers: {
        header: token,
      },
    });
    setUser(null);
    return result;
  };

  const getUserByTokenCalled = useRef(false);

  const getUserByTolken = async () => {
    if (getUserByTokenCalled.current) return;
    getUserByTokenCalled.current = true;

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        console.warn("No token found! Logging out...");
        setUser(null);
        setIsLogedIn(false);
        logout();
        return;
      }

      const result = await axiosBaseURL.get("/fe/getUser", {
        headers: {
          header: token,
          Authorization: `Bearer ${token}`,
        },
      });

      if (result.data) {
        setUser(result.data);
        setIsLogedIn(true);
      } else {
        console.error("Invalid user data received.");
        setUser(null);
        setIsLogedIn(false);
      }
    } catch (err) {
      console.error("Error fetching user:", err);
      setUser(null);
      setIsLogedIn(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token && !user) {
      getUserByTolken();
    }
  }, []);

  const _changePassword = (body: {
    header: string;
    userName: string;
    hashedOldPassword: string;
    hashedNewPassword: string;
  }) => {
    const result = axiosBaseURL.post(
      "/fe/changePassword",
      {
        userName: body.userName,
        hashedOldPassword: body.hashedOldPassword,
        hashedNewPassword: body.hashedNewPassword,
      },
      { headers: { header: body.header } }
    );
    return result;
  };

  const {
    data,
    mutate,
    error,
    isPending: signingIn,
  } = useMutation({
    mutationKey: ["signin"],
    mutationFn: signin,
  });

  const logoutMutation = useMutation({
    mutationKey: ["logout"],
    mutationFn: signout,
  });

  useEffect(() => {
    if (data?.data) {
      if (data.data.status === 1) {
        setUser(data.data);
      }
      setLoaginError({ status: data.data.status });
    }
  }, [data]);

  const login = (usrName: string, pswd: string) => {
    setUserName(usrName);
    setPassword(pswd);
    if (userName && password) {
      const auth_str = window.btoa(usrName + ":" + pswd);
      setAuthString(`Basic ${auth_str}`);
      mutate();
    }
  };
  const logout = () => {
    logoutMutation.mutate(localStorage.getItem("token") || ""); // Send token if available
    setIsLogedIn(false);
    setUser(undefined);
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    // navigate("/login");
  };
  const changepasswordMutation = useMutation({
    mutationKey: ["changePassword"],
    mutationFn: _changePassword,
  });

  const changePassword = (
    header: string,
    {
      userName,
      hashedOldPassword,
      hashedNewPassword,
    }: {
      userName: string;
      hashedOldPassword: string;
      hashedNewPassword: string;
    }
  ) => {
    changepasswordMutation.mutate({
      header,
      userName,
      hashedOldPassword,
      hashedNewPassword,
    });
    return {
      isSuccess: changepasswordMutation.isSuccess,
      isError: changepasswordMutation.isError,
    };
  };
  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        authString,
        isLogedIn,
        signingIn,
        loginError,
        login,
        logout,
        changePassword,
        getUserByTolken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
